// src/components/SpeedDetailModal.tsx - FIXED HOOKS ORDER

import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  InputAdornment,
  Chip,
  Typography,
  IconButton,
  Paper,
  Stack,
  alpha,
  useTheme,
  Tooltip,
} from '@mui/material';
import {
  Close as CloseIcon,
  Search as SearchIcon,
  FileDownload as DownloadIcon,
  Speed as SpeedIcon,
  Warning as WarningIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';

interface Vehicle {
  imei: string;
  plateNumber: string;
  make?: string;
  model?: string;
  avgSpeed: number;
  maxSpeed: number;
  violations: number;
}

interface SpeedDetailModalProps {
  open: boolean;
  onClose: () => void;
  date: string;
  data: {
    avgSpeed: number;
    maxSpeed: number;
    violations: number;
    vehicleCount: number;
    vehicles: Vehicle[];
  } | null;
  viewMode: 'average' | 'max' | 'violations';
}

type SortField = 'plateNumber' | 'avgSpeed' | 'maxSpeed' | 'violations';
type SortOrder = 'asc' | 'desc';

const SpeedDetailModal: React.FC<SpeedDetailModalProps> = ({
  open,
  onClose,
  date,
  data,
  viewMode,
}) => {
  const theme = useTheme();
  const [sortField, setSortField] = useState<SortField>('avgSpeed');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [searchQuery, setSearchQuery] = useState('');

  // ✅ FIXED: useMemo must be before any early returns
  const filteredAndSortedVehicles = useMemo(() => {
    if (!data || !data.vehicles) return [];

    let filtered = data.vehicles.filter((vehicle) =>
      vehicle.plateNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
      vehicle.make?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      vehicle.model?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    filtered.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      // Handle string comparison for plateNumber
      if (sortField === 'plateNumber') {
        aVal = aVal as string;
        bVal = bVal as string;
        return sortOrder === 'asc'
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      // Numeric comparison
      aVal = Number(aVal) || 0;
      bVal = Number(bVal) || 0;
      return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
    });

    return filtered;
  }, [data, searchQuery, sortField, sortOrder]);

  // ✅ NOW early return is after all hooks
  if (!data) return null;

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  // Export to CSV
  const handleExport = () => {
    const headers = ['Vehicle', 'Make/Model', 'Avg Speed (km/h)', 'Max Speed (km/h)', 'Violations'];
    const rows = filteredAndSortedVehicles.map(v => [
      v.plateNumber,
      `${v.make || ''} ${v.model || ''}`.trim(),
      v.avgSpeed.toFixed(1),
      v.maxSpeed,
      v.violations,
    ]);

    const csv = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `speed-analytics-${date}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const getSeverityColor = (violations: number) => {
    if (violations > 50) return 'error';
    if (violations > 20) return 'warning';
    return 'default';
  };

  const getSpeedColor = (speed: number) => {
    if (speed > 100) return theme.palette.error.main;
    if (speed > 80) return theme.palette.warning.main;
    return theme.palette.success.main;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '90vh',
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          py: 2,
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight="bold">
            Speed Analytics Details
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9 }}>
            {new Date(date).toLocaleDateString('en-US', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* Summary Cards */}
      <Box sx={{ p: 3, bgcolor: alpha(theme.palette.primary.main, 0.03) }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <Paper
            elevation={0}
            sx={{
              flex: 1,
              p: 2,
              border: `2px solid ${alpha(theme.palette.info.main, 0.2)}`,
              bgcolor: alpha(theme.palette.info.main, 0.05),
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <TrendingUpIcon sx={{ color: 'info.main', fontSize: 32 }} />
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Fleet Avg Speed
                </Typography>
                <Typography variant="h5" fontWeight="bold" color="info.main">
                  {data.avgSpeed.toFixed(1)} km/h
                </Typography>
              </Box>
            </Stack>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              flex: 1,
              p: 2,
              border: `2px solid ${alpha(theme.palette.error.main, 0.2)}`,
              bgcolor: alpha(theme.palette.error.main, 0.05),
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <SpeedIcon sx={{ color: 'error.main', fontSize: 32 }} />
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Max Speed
                </Typography>
                <Typography variant="h5" fontWeight="bold" color="error.main">
                  {data.maxSpeed} km/h
                </Typography>
              </Box>
            </Stack>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              flex: 1,
              p: 2,
              border: `2px solid ${alpha(theme.palette.warning.main, 0.2)}`,
              bgcolor: alpha(theme.palette.warning.main, 0.05),
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <WarningIcon sx={{ color: 'warning.main', fontSize: 32 }} />
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Total Violations
                </Typography>
                <Typography variant="h5" fontWeight="bold" color="warning.main">
                  {data.violations}
                </Typography>
              </Box>
            </Stack>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              flex: 1,
              p: 2,
              border: `2px solid ${alpha(theme.palette.success.main, 0.2)}`,
              bgcolor: alpha(theme.palette.success.main, 0.05),
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Typography variant="h3" color="success.main">
                {data.vehicleCount}
              </Typography>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Vehicles Active
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </Stack>
      </Box>

      <DialogContent sx={{ p: 3 }}>
        {/* Search and Export */}
        <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search by vehicle plate, make, or model..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <Tooltip title="Export to CSV">
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleExport}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Export
            </Button>
          </Tooltip>
        </Box>

        {/* Results Count */}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Showing {filteredAndSortedVehicles.length} of {data.vehicles.length} vehicles
        </Typography>

        {/* Table */}
        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{ maxHeight: 400, overflow: 'auto' }}
        >
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: 'action.hover' }}>
                  <TableSortLabel
                    active={sortField === 'plateNumber'}
                    direction={sortField === 'plateNumber' ? sortOrder : 'asc'}
                    onClick={() => handleSort('plateNumber')}
                  >
                    Vehicle
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: 'action.hover' }}>
                  Make / Model
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: 'action.hover' }}>
                  <TableSortLabel
                    active={sortField === 'avgSpeed'}
                    direction={sortField === 'avgSpeed' ? sortOrder : 'asc'}
                    onClick={() => handleSort('avgSpeed')}
                  >
                    Avg Speed
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: 'action.hover' }}>
                  <TableSortLabel
                    active={sortField === 'maxSpeed'}
                    direction={sortField === 'maxSpeed' ? sortOrder : 'asc'}
                    onClick={() => handleSort('maxSpeed')}
                  >
                    Max Speed
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: 'action.hover' }}>
                  <TableSortLabel
                    active={sortField === 'violations'}
                    direction={sortField === 'violations' ? sortOrder : 'asc'}
                    onClick={() => handleSort('violations')}
                  >
                    Violations
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredAndSortedVehicles.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      No vehicles found matching "{searchQuery}"
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredAndSortedVehicles.map((vehicle, index) => (
                  <TableRow
                    key={vehicle.imei}
                    sx={{
                      '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.05) },
                      bgcolor: index % 2 === 0 ? 'background.paper' : 'action.hover',
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight="600">
                        {vehicle.plateNumber}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {vehicle.make} {vehicle.model}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Chip
                        label={`${vehicle.avgSpeed.toFixed(1)} km/h`}
                        size="small"
                        sx={{
                          fontWeight: 600,
                          bgcolor: alpha(getSpeedColor(vehicle.avgSpeed), 0.1),
                          color: getSpeedColor(vehicle.avgSpeed),
                        }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Chip
                        label={`${vehicle.maxSpeed} km/h`}
                        size="small"
                        sx={{
                          fontWeight: 600,
                          bgcolor: alpha(getSpeedColor(vehicle.maxSpeed), 0.1),
                          color: getSpeedColor(vehicle.maxSpeed),
                        }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      {vehicle.violations > 0 ? (
                        <Chip
                          label={vehicle.violations}
                          size="small"
                          color={getSeverityColor(vehicle.violations)}
                          icon={<WarningIcon fontSize="small" />}
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, bgcolor: 'action.hover' }}>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SpeedDetailModal;